// src/components/Drive/DeliveryReview.jsx
import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Stack, Chip,
  TextField, CircularProgress, Collapse,
} from "@mui/material";
import { Star } from "lucide-react";
import { submitDeliveryReviewFn } from "../../firebase/deliveryFunctions";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const msToLocal = (ms) => {
  if (!ms) return "";
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ─── Star rating ──────────────────────────────────────────────────────────────

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Box
          key={n}
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          sx={{
            cursor: "pointer", display: "flex",
            color: n <= (hover || value) ? "warning.main" : "action.disabled",
            transition: "color 0.1s",
          }}
        >
          <Star size={28} fill={n <= (hover || value) ? "currentColor" : "none"} />
        </Box>
      ))}
    </Box>
  );
}

// ─── CheckRow ─────────────────────────────────────────────────────────────────
// When marked as issue, shows a correction field typed to match the original value.
//
// fieldType:
//   "address"  → single-line text
//   "datetime" → datetime-local input showing current value
//   "number"   → numeric input showing current value
//   "text"     → plain multiline (general notes)

function CheckRow({ label, checked, currentValue, correctedValue, fieldType = "text", onToggle, onCorrect }) {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600}>{label}</Typography>
          {currentValue != null && currentValue !== "" && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {currentValue}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>
          <Chip
            label="OK"
            size="small"
            color={checked ? "success" : "default"}
            variant={checked ? "filled" : "outlined"}
            onClick={() => onToggle(true)}
            sx={{ cursor: "pointer", fontWeight: 700, fontSize: "0.7rem" }}
          />
          <Chip
            label="Ongelma"
            size="small"
            color={!checked ? "error" : "default"}
            variant={!checked ? "filled" : "outlined"}
            onClick={() => onToggle(false)}
            sx={{ cursor: "pointer", fontWeight: 700, fontSize: "0.7rem" }}
          />
        </Box>
      </Box>

      <Collapse in={!checked}>
        <Box sx={{ mt: 1 }}>
          {fieldType === "datetime" && (
            <TextField
              size="small"
              fullWidth
              type="datetime-local"
              label="Oikea arvo"
              value={correctedValue ?? ""}
              onChange={(e) => onCorrect(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          )}
          {fieldType === "number" && (
            <TextField
              size="small"
              fullWidth
              type="number"
              label="Oikea arvo"
              value={correctedValue ?? ""}
              onChange={(e) => onCorrect(e.target.value)}
            />
          )}
          {fieldType === "address" && (
            <TextField
              size="small"
              fullWidth
              label="Oikea osoite"
              placeholder="Kirjoita oikea osoite..."
              value={correctedValue ?? ""}
              onChange={(e) => onCorrect(e.target.value)}
            />
          )}
          {fieldType === "text" && (
            <TextField
              size="small"
              fullWidth
              multiline
              rows={2}
              label="Lisätietoja"
              placeholder="Kuvaile ongelma..."
              value={correctedValue ?? ""}
              onChange={(e) => onCorrect(e.target.value)}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export function DeliveryReview({ delivery, open, onClose, onSubmitted }) {
  const [stars, setStars]         = useState(5);
  const [checks, setChecks]       = useState({ origin: true, destination: true, times: true, reward: true });
  const [corrected, setCorrected] = useState({ origin: "", destination: "", pickupTime: "", targetTime: "", reward: "" });
  const [generalNote, setGeneralNote] = useState("");
  const [saving, setSaving]       = useState(false);

  if (!delivery) return null;

  const origin      = delivery.originInfo?.address      || delivery.origin      || "";
  const destination = delivery.destinationInfo?.address || delivery.destination || "";
  const pickupTime  = msToLocal(delivery.pickupTime  ?? delivery.pickUpTime);
  const targetTime  = msToLocal(delivery.targetTime);
  const reward      = delivery.reward != null ? String(delivery.reward) : "";

  const setCheck   = (key, val) => setChecks((c) => ({ ...c, [key]: val }));
  const setCorrect = (key, val) => setCorrected((c) => ({ ...c, [key]: val }));

  const hasIssue = Object.values(checks).some((v) => !v);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await submitDeliveryReviewFn(delivery.id ?? delivery.deliveryId, {
        stars,
        checks,
        // Only send corrected values that the user actually filled in
        corrections: {
          origin:      !checks.origin      && corrected.origin.trim()      ? corrected.origin.trim()      : null,
          destination: !checks.destination && corrected.destination.trim() ? corrected.destination.trim() : null,
          pickupTime:  !checks.times       && corrected.pickupTime         ? new Date(corrected.pickupTime).getTime()  : null,
          targetTime:  !checks.times       && corrected.targetTime         ? new Date(corrected.targetTime).getTime()  : null,
          reward:      !checks.reward      && corrected.reward !== ""      ? Number(corrected.reward)     : null,
        },
        generalNote: generalNote.trim() || null,
        dispute: hasIssue,
      });
      onSubmitted?.();
      onClose();
    } catch (err) {
      console.error(err);
      alert(err.message || "Arviointi epäonnistui");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        Arvioi kuljetus
        <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mt: 0.25 }}>
          {origin} → {destination}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>

          {/* Stars */}
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.8, display: "block", mb: 1 }}>
              Yleisarvosana
            </Typography>
            <StarRating value={stars} onChange={setStars} />
          </Box>

          {/* Typed check rows */}
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.8, display: "block", mb: 1.5 }}>
              Kuljetuksen tiedot
            </Typography>
            <Stack spacing={2}>
              <CheckRow
                label="Nouto-osoite"
                checked={checks.origin}
                currentValue={origin}
                correctedValue={corrected.origin}
                fieldType="address"
                onToggle={(v) => setCheck("origin", v)}
                onCorrect={(v) => setCorrect("origin", v)}
              />
              <CheckRow
                label="Toimitusosoite"
                checked={checks.destination}
                currentValue={destination}
                correctedValue={corrected.destination}
                fieldType="address"
                onToggle={(v) => setCheck("destination", v)}
                onCorrect={(v) => setCorrect("destination", v)}
              />
              <CheckRow
                label="Noutoaika"
                checked={checks.times}
                currentValue={pickupTime.replace("T", " ")}
                correctedValue={corrected.pickupTime}
                fieldType="datetime"
                onToggle={(v) => setCheck("times", v)}
                onCorrect={(v) => setCorrect("pickupTime", v)}
              />
              {/* targetTime shares the same "times" check — shown when times is flagged */}
              <Collapse in={!checks.times}>
                <CheckRow
                  label="Kohdeaika"
                  checked={checks.times}
                  currentValue={targetTime.replace("T", " ")}
                  correctedValue={corrected.targetTime}
                  fieldType="datetime"
                  onToggle={(v) => setCheck("times", v)}
                  onCorrect={(v) => setCorrect("targetTime", v)}
                />
              </Collapse>
              <CheckRow
                label="Palkkio"
                checked={checks.reward}
                currentValue={reward ? `${reward} €` : ""}
                correctedValue={corrected.reward}
                fieldType="number"
                onToggle={(v) => setCheck("reward", v)}
                onCorrect={(v) => setCorrect("reward", v)}
              />
            </Stack>
          </Box>

          {/* General note — always visible */}
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.8, display: "block", mb: 1 }}>
              Lisätietoja
            </Typography>
            <TextField
              size="small"
              fullWidth
              multiline
              rows={2}
              placeholder="Vapaamuotoinen palaute..."
              value={generalNote}
              onChange={(e) => setGeneralNote(e.target.value)}
            />
          </Box>

          {hasIssue && (
            <Box sx={{ p: 1.5, border: "1px solid", borderColor: "error.light", borderRadius: 1 }}>
              <Typography variant="caption" color="error.main" fontWeight={600}>
                Käsittelemme palautteesi mahdollisimman pian!
              </Typography>
            </Box>
          )}

        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">Peruuta</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disableElevation
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? "Lähetetään..." : "Lähetä arviointi"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}